import { useState } from 'react';

import type { QuoteState } from '../hooks/useQuote';
import PendingValue from './PendingValue';

/**
 * The discount code field.
 *
 * Compact by design: a label, an input, an Apply button, and one line of feedback. It
 * sits beside the total because that is the number it changes.
 *
 * ---------------------------------------------------------------------------
 * IT VALIDATES NOTHING AND CALCULATES NOTHING.
 *
 * Applying a code re-quotes. The server resolves it, decides whether it is valid, works
 * out the amount, and returns the new authoritative total; this component renders that
 * answer. There is no client-side code format, no optimistic discount, and no local
 * arithmetic -- so the figure on screen is always one the server actually stands behind.
 *
 * A rejected code does NOT blank the price: the quote comes back undiscounted with the
 * reason attached, so the customer keeps a real total and gets an inline error.
 *
 * Applying here grants nothing either. Order creation resolves the code again from
 * scratch, and a code that lapses in between is refused at checkout rather than quietly
 * dropped -- see backend/src/services/orderService.js.
 */

interface DiscountCodeFieldProps {
  /** The code currently applied to the quote, or null. */
  appliedCode: string | null;
  quoteState: QuoteState;
  onApply: (code: string) => void;
  onRemove: () => void;
  currency: string;
  disabled?: boolean;
}

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

export default function DiscountCodeField({
  appliedCode,
  quoteState,
  onApply,
  onRemove,
  currency,
  disabled = false,
}: DiscountCodeFieldProps) {
  const [value, setValue] = useState('');

  const quote = quoteState.status === 'ready' ? quoteState.quote : null;
  const discount = quote?.discount ?? null;

  const applied = discount?.applied === true ? discount : null;
  // The server's own words. Only shown while a code is actually being attempted.
  const rejection = discount?.applied === false ? discount.reason : null;

  const pending = appliedCode !== null && quoteState.status === 'loading';

  /*
   * THE APPLIED CARD SURVIVES A RE-QUOTE.
   *
   * `discount` comes off the quote, so it was null the moment a new one went in flight —
   * and this component fell back to the input form, replacing the applied card with a
   * text field and a button on every drag of the slider. That was a genuine remount of a
   * focusable control, not just a repaint.
   *
   * The card now stays for as long as a code is applied. The AMOUNT does not: it is
   * recomputed against the new subtotal, so showing the previous one would be a stale
   * money figure beside a configuration it no longer describes. It is withheld and its
   * space reserved instead.
   */
  const showAppliedCard = applied !== null || pending;

  function submit() {
    const code = value.trim();
    if (code === '') return;
    onApply(code);
  }

  if (showAppliedCard) {
    return (
      <div className="mm-discount" data-testid="discount-applied">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">
              Code <span className="mm-numeric">{applied?.code ?? appliedCode}</span> applied
            </p>
            <p className="mm-help mt-0.5 text-xs" data-testid="discount-amount">
              {applied ? (
                <>−{formatCents(applied.amountCents ?? 0, currency)} off your order</>
              ) : (
                /* Recomputed against the new subtotal. Withheld, not carried over. */
                <PendingValue width="15ch" />
              )}
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              setValue('');
              onRemove();
            }}
            data-testid="discount-remove"
            className="text-xs font-medium underline"
            style={{ color: 'var(--mm-text-muted)' }}
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mm-discount">
      <label htmlFor="discount-code" className="mm-label text-sm">
        Discount code
      </label>

      <div className="mt-2 flex gap-2">
        <input
          id="discount-code"
          name="discountCode"
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          // Enter applies the code rather than submitting the order -- this field sits
          // inside the order form, and a stray Enter must not create an order.
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
          placeholder="Enter code"
          aria-describedby={rejection ? 'discount-error' : undefined}
          aria-invalid={rejection ? true : undefined}
          disabled={disabled}
          className="mm-input flex-1"
          data-testid="discount-input"
        />

        <button
          type="button"
          onClick={submit}
          disabled={disabled || pending || value.trim() === ''}
          data-testid="discount-apply"
          className="mm-btn-secondary shrink-0"
        >
          {pending ? 'Applying…' : 'Apply'}
        </button>
      </div>

      {rejection && (
        <p
          id="discount-error"
          role="alert"
          data-testid="discount-error"
          className="mm-help mt-2 text-xs"
          style={{ color: 'var(--mm-danger)' }}
        >
          {rejection}
        </p>
      )}
    </div>
  );
}
